// Copyright ©️ 2026 eVolve MEP, LLC

import { Stack, Text, Title, Flex } from '@mantine/core';

import { MobileBasePageHeader } from 'components/Mantine/MobileBasePageHeader';
import { MobileLinkButtonGroup } from 'components/Mantine/MobileLinkButtonGroup';
import { WorkOrderItemsList } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemsList';
import { WorkOrderDetailModalProvider } from 'modules/Shop/WorkOrders/WorkOrderDetail/modalRegistries';
import { useWorkOrderDetailMutations } from 'modules/Shop/WorkOrders/WorkOrderDetail/useWorkOrderDetailMutations';
import type { WorkOrderDetailPageProps } from 'modules/Shop/WorkOrders/WorkOrderDetail/WorkOrderDetailPage';
import { MobileWorkOrderActions } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/MobileWorkOrderActions';

export const MobileWorkOrderDetailPage = ({ workOrder, onWorkOrderUpdated }: WorkOrderDetailPageProps) => {
  const { refreshWorkOrder } = useWorkOrderDetailMutations(workOrder, onWorkOrderUpdated);

  return (
    <WorkOrderDetailModalProvider>
      <Stack flex={1} mih={0} w="100%" p="md" pb={0} gap="xs">
        <MobileBasePageHeader
          title={workOrder.identifier}
          parentPage={{ title: 'Work Orders', url: '/work-orders' }}
          rightSideComponent={<MobileWorkOrderActions workOrder={workOrder} onWorkOrderUpdated={onWorkOrderUpdated} />}
        />
        <Stack flex={1} style={{ overflowY: 'auto' }} gap="md">
          <Stack gap={0}>
            <Text c="dimmed" fz="sm">
              {workOrder.identifier}
            </Text>
            <Title order={3}>{workOrder.name}</Title>
          </Stack>

          {!!workOrder.description && <Text style={{ whiteSpace: 'pre-wrap' }}>{workOrder.description}</Text>}
          <MobileLinkButtonGroup
            from={workOrder.identifier}
            linkButtons={[
              {
                statusBadgeProps: {
                  customStatusEntityName: 'Work Order',
                  stateName: workOrder.workOrderStateName,
                  statusId: workOrder.workOrderStatusId,
                },
                date: workOrder.needByDate,
                assignees: workOrder.assignees,
                to: `/work-orders/${workOrder.workOrderId}/details`,
              },
              {
                label: (
                  <Flex gap="xs" align="center">
                    Attachments
                    <Text c="dimmed">({workOrder.totalAttachmentCount})</Text>
                  </Flex>
                ),
                to: `/work-orders/${workOrder.workOrderId}/attachments`,
              },
              { label: 'Activity', to: `/work-orders/${workOrder.workOrderId}/activity` },
            ]}
          />

          <WorkOrderItemsList
            workOrder={workOrder}
            disableSelection
            forceWorkOrderRefresh={refreshWorkOrder}
            onItemAttachmentsChanged={refreshWorkOrder}
            onTaskAttachmentsChanged={refreshWorkOrder}
          />
        </Stack>
      </Stack>
    </WorkOrderDetailModalProvider>
  );
};
